import React from "react";
import { Mail, Globe, MapPin, ShieldCheck } from "lucide-react";
import { TransformedProfileData } from "@/types/publicProfile";
import { isSectionVisible } from "../utils";
import { StatsGrid } from "./StatsGrid";

interface HeroSectionProps {
  profileData: TransformedProfileData;
  downloadVCard: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  profileData,
  downloadVCard,
}) => {
  const data = profileData;

  return (
    <section className="relative overflow-hidden min-h-[88dvh] flex items-center pt-[12dvh]">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br  from-slate-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-black">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 dark:bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 dark:bg-purple-400/10 rounded-full blur-3xl"></div>
      </div>

      {/* Enhanced Scroll indicator */}
      <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 opacity-70 hover:opacity-100 transition-opacity duration-300">
        <div className="flex flex-col items-center space-y-3">
          {/* Modern scroll mouse icon */}
          <div className="relative">
            <div className="w-6 h-10 border-2 border-gray-500 dark:border-gray-400 rounded-full bg-white/10 dark:bg-black/10 backdrop-blur-sm shadow-lg">
              <div
                className="absolute top-2 left-1/2 transform -translate-x-1/2 w-1 h-2 bg-gray-600 dark:bg-gray-300 rounded-full"
                style={{
                  animation: "scroll-indicator 2s infinite ease-in-out",
                }}
              ></div>
            </div>
            {/* Glow effect */}
            <div className="absolute inset-0 w-6 h-10 border-2 border-blue-400/30 dark:border-blue-300/30 rounded-full animate-pulse"></div>
          </div>

          {/* Scroll text */}
          <div className="flex flex-col items-center space-y-1">
            <p className="text-xs text-gray-600 dark:text-gray-300 font-medium tracking-wide">
              Scroll to explore
            </p>
            {/* Chevron arrows */}
            <div
              className="flex flex-col space-y-0.5"
              style={{
                animation: "gentle-bounce 3s infinite ease-in-out",
              }}
            >
              <svg
                className="w-3 h-2 text-gray-500 dark:text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <svg
                className="w-3 h-2 text-gray-400 dark:text-gray-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Embedded CSS for custom animations */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes scroll-indicator {
            0% {
              transform: translateX(-50%) translateY(8px);
              opacity: 0;
            }
            50% {
              opacity: 1;
            }
            100% {
              transform: translateX(-50%) translateY(-4px);
              opacity: 0;
            }
          }
          
          @keyframes gentle-bounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-3px);
            }
          }
        `,
        }}
      />

      <div className="relative mx-auto max-w-6xl px-4 py-16 md:px-6 lg:px-8 w-full">
        <div className="flex flex-col items-center text-center md:grid md:items-center md:gap-12 md:grid-cols-12 md:text-left">
          {/* Mobile: Profile Image First */}
          <div className="order-1 md:order-2 md:col-span-5 flex justify-center md:justify-end mb-8 md:mb-0">
            <div className="relative">
              <div className="relative h-48 w-48 sm:h-56 sm:w-56 md:h-72 md:w-72 lg:h-80 lg:w-80 overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 p-1.5 sm:p-2 shadow-2xl ring-1 ring-gray-200 dark:ring-gray-800">
                <img
                  src={data.personalInfo.avatar}
                  alt={data.personalInfo.name}
                  className="h-full w-full rounded-[1rem] sm:rounded-[1.5rem] object-cover"
                />
                <div className="absolute inset-0 rounded-[1rem] sm:rounded-[1.5rem] bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none"></div>
              </div>

              {/* Subtle glow effect */}
              <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 blur-2xl -z-10 opacity-60"></div>
            </div>
          </div>

          {/* Mobile: Content Below Image */}
          <div className="order-2 md:order-1 md:col-span-7 space-y-6 sm:space-y-8 w-full">
            {/* Enhanced name typography */}
            <div className="space-y-3 sm:space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 dark:text-white">
                {data.personalInfo.name}
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-300 font-normal leading-relaxed">
                {data.personalInfo.headline}
              </p>
            </div>

            {/* Refined status indicators */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
              {/* Location Badge - Only show if location exists */}
              {data.personalInfo.location &&
                data.personalInfo.location.trim() && (
                  <div className="inline-flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-700">
                    <MapPin className="h-3 w-3 text-gray-500" />
                    {data.personalInfo.location}
                  </div>
                )}
              <div
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border ${
                  data.contactInfo.availableForWork
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
                    : "bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800"
                }`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    data.contactInfo.availableForWork
                      ? "bg-green-500"
                      : "bg-orange-500"
                  }`}
                ></div>
                {data.contactInfo.availableForWork
                  ? "Available for work"
                  : "Currently unavailable"}
              </div>
              <div className="inline-flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2.5 py-1.5 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-800">
                <ShieldCheck className="h-3 w-3" />
                Verified
              </div>
            </div>

            {/* Premium action buttons */}
            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              <div
                onClick={downloadVCard}
                className="group relative inline-flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2.5 rounded-full font-medium shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 ease-out text-sm"
              >
                <Mail className="h-4 w-4" />
                Save to contact
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/0 to-purple-600/0 group-hover:from-blue-600/20 group-hover:to-purple-600/20 transition-all duration-300"></div>
              </div>

              {typeof window !== "undefined" &&
                /Mobi|Android/i.test(window.navigator.userAgent) && (
                  <a
                    href={"connectifyz://?profile=" + data.id}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 px-4 py-2.5 rounded-full font-medium shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 ease-out text-sm"
                  >
                    <Globe className="h-4 w-4" />
                    Connect
                  </a>
                )}
            </div>

            {isSectionVisible(data.sections.stats.privacy) && (
              <StatsGrid stats={data.sections.stats.items} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
